import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { X, Check } from 'lucide-react';

const RevolutionarySection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <section 
      id="revolutionary" 
      ref={ref}
      className="section-spacing relative section-dark overflow-hidden"
      data-testid="revolutionary-section"
    >
      <div className="section-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#60A5FA] text-sm font-medium tracking-wider uppercase mb-4 block">
            {t.revolutionary.subtitle}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white max-w-3xl mx-auto">
            {t.revolutionary.title}
          </h2>
          {t.revolutionary.titleHighlight && (
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold max-w-3xl mx-auto mt-2" style={{
              background: 'linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {t.revolutionary.titleHighlight}
            </h2>
          )}
        </motion.div>
        
        {/* Comparison Grid */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Traditional Side */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass-dark rounded-2xl p-8 opacity-70"
            data-testid="comparison-traditional"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <X className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-[#94A3B8]">
                {t.revolutionary.comparison.traditional}
              </h3>
            </div>
            
            <div className="space-y-4">
              {t.revolutionary.comparison.items.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 text-[#64748B]"
                >
                  <X className="w-5 h-5 text-red-400/50 mt-0.5 flex-shrink-0" />
                  <span>{item.traditional}</span>
                </div>
              ))}
            </div>
          </motion.div>
          
          {/* AQERA Side */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="glass-dark rounded-2xl p-8 border border-[#2563EB]/30 relative overflow-hidden"
            style={{ boxShadow: '0 0 40px -10px rgba(37, 99, 235, 0.3)' }}
            data-testid="comparison-aqera"
          >
            {/* Glow Effect */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#2563EB]/20 blur-3xl" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#2563EB]/20 flex items-center justify-center">
                  <Check className="w-5 h-5 text-[#60A5FA]" />
                </div>
                <h3 className="text-xl font-bold text-white">
                  {t.revolutionary.comparison.aqera}
                </h3>
              </div>
              
              <div className="space-y-4">
                {t.revolutionary.comparison.items.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 text-[#94A3B8]"
                  >
                    <Check className="w-5 h-5 text-[#60A5FA] mt-0.5 flex-shrink-0" />
                    <span>{item.aqera}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default RevolutionarySection;
